import React from "react";
import {
  Avatar,
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import ReadOnlyIngredientRow from "./ReadOnlyIngredientRow";
import ReadOnlyYieldDisplay from "./ReadOnlyYieldDisplay";
import utilFunctions from "../../../utilities/utilFunctions";

const SpecRecipeForm = ({ recipe, specPartners }) => {
  const tableCellStyle = {
    padding: 0,
    border: "1px solid",
    borderBottom: "none",
    borderColor: (theme) => theme.palette.terrain[400],
    backgroundColor: (theme) => theme.palette.pure.white,
  };

  const tableHeadStyle = {
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 2,
    background: (theme) => theme.palette.terrain[300],
    height: "24px",
    border: "1px solid",
    borderBottom: "none",
    borderColor: (theme) => theme.palette.terrain[400],
    borderTop: "none",
    lineHeight: 1.2,
    fontSize: 10,
    fontWeight: 600,
  };

  const partner = specPartners?.find((p) => p.id === recipe?.specPartnerId);

  return (
    <Box>
      {/* Recipe Card: Image + Details */}
      <Box
        sx={{
          marginTop: 4,
          marginBottom: 2,
          border: "1px solid",
          borderColor: (theme) => theme.palette.terrain[300],
          borderRadius: 4,
          padding: 4,
          backgroundColor: (theme) => theme.palette.pure.white,
        }}
      >
        <Grid container spacing={4} alignItems="flex-start">
          {/* Image */}
          <Grid item>
            <Box
              sx={{
                width: "200px",
                height: "200px",
                borderRadius: 4,
                border: "2px solid",
                borderColor: (theme) => theme.palette.terrain[300],
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              {recipe?.imageUrl ? (
                <Avatar
                  variant="rounded"
                  sx={{ width: "200px", height: "200px" }}
                  src={recipe.imageUrl}
                />
              ) : (
                <AddPhotoAlternateOutlinedIcon
                  sx={{
                    fontSize: "48px",
                    color: (theme) => theme.palette.terrain[400],
                  }}
                />
              )}
            </Box>
          </Grid>

          {/* Recipe Details */}
          <Grid item xs>
            <Typography variant="h2" sx={{ marginBottom: 2 }}>
              {utilFunctions.capitalizeString(recipe?.name || "")}
            </Typography>

            {recipe?.description && (
              <Typography variant="body1" sx={{ marginBottom: 2 }}>
                {recipe.description}
              </Typography>
            )}

            <Box
              sx={{ display: "flex", alignItems: "center", marginBottom: 2 }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, marginRight: 1 }}
              >
                Yield:
              </Typography>
              <ReadOnlyYieldDisplay recipe={recipe} compact />
            </Box>

            {partner && (
              <Box sx={{ display: "flex", alignItems: "center", marginTop: 2 }}>
                <Avatar
                  variant="rounded"
                  src={partner.imageUrl}
                  sx={{ width: 32, height: 32, marginRight: 3, borderRadius: 1 }}
                />
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Created By:{" "}
                  <Typography component="span" variant="body2" sx={{ fontWeight: 400 }}>
                    {partner.name}
                  </Typography>
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>

      {/* Ingredients Section */}
      <Box sx={{ marginTop: 8 }}>
        <Box sx={{ marginBottom: 4 }}>
          <Typography variant="h3">Ingredients</Typography>
        </Box>
        <Grid
          item
          sx={{ width: "100%", display: "flex", flexDirection: "row" }}
        >
          <TableContainer
            sx={{
              overflow: "auto",
              borderRadius: 2,
              border: "1px solid",
              borderColor: (theme) => theme.palette.terrain[400],
            }}
          >
            <Table
              sx={{ width: "100%", overflow: "auto" }}
              aria-label="ingredients table"
            >
              <TableHead sx={{ height: "24px" }}>
                <TableRow sx={{ height: 24 }}>
                  <TableCell
                    sx={[tableHeadStyle, { width: "80px", borderLeft: "none" }]}
                  >
                    Quantity
                  </TableCell>
                  <TableCell
                    sx={[tableHeadStyle, { width: "250px", minWidth: "250px" }]}
                  >
                    Ingredient Name
                  </TableCell>
                  <TableCell
                    sx={[
                      tableHeadStyle,
                      {
                        width: "56px",
                        textAlign: "center",
                        padding: 0,
                        borderRight: "none",
                      },
                    ]}
                  >
                    <Box
                      component="div"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        paddingLeft: 1,
                        paddingRight: 1,
                      }}
                    >
                      Garnish
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recipe?.ingredients?.map((x) => (
                  <ReadOnlyIngredientRow
                    key={x.id}
                    ingredient={x}
                    tableCellStyle={tableCellStyle}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Box>

      {/* Training Details */}
      <Box sx={{ marginTop: 8 }}>
        <Box sx={{ marginBottom: 4 }}>
          <Typography variant="h3">Training Details</Typography>
        </Box>
        <Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              sx={[
                tableHeadStyle,
                {
                  width: "50%",
                  display: "flex",
                  alignItems: "center",
                  border: "1px solid",
                  borderColor: (theme) => theme.palette.terrain[400],
                  borderTopLeftRadius: 8,
                },
              ]}
            >
              Tasting Notes
            </Box>
            <Box
              sx={[
                tableHeadStyle,
                {
                  width: "50%",
                  display: "flex",
                  alignItems: "center",
                  border: "1px solid",
                  borderLeft: "none",
                  borderColor: (theme) => theme.palette.terrain[400],
                  borderTopRightRadius: 8,
                },
              ]}
            >
              Directions
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "row" }}>
            <Box
              sx={[
                tableCellStyle,
                {
                  width: "50%",
                  border: "1px solid",
                  borderColor: (theme) => theme.palette.terrain[400],
                  borderTop: "none",
                  borderBottomLeftRadius: 8,
                  padding: 4,
                  minHeight: "120px",
                },
              ]}
            >
              <Typography variant="body1">
                {recipe?.tastingNotes || "—"}
              </Typography>
            </Box>
            <Box
              sx={[
                tableCellStyle,
                {
                  width: "50%",
                  border: "1px solid",
                  borderLeft: "none",
                  borderColor: (theme) => theme.palette.terrain[400],
                  borderTop: "none",
                  borderBottomRightRadius: 8,
                  padding: 4,
                  minHeight: "120px",
                },
              ]}
            >
              <Typography variant="body1">
                {recipe?.directions || "—"}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SpecRecipeForm;
